/**
 * 液化提交：把位移场烘进图层像素（ADR-0013）。
 *
 * 结构对齐 `engine/transform/bake.ts`（展平源区域→wasm 重采样→touch/capture/
 * 写回），但更简单：液化不改变图层外接矩形，源和目标是同一张矩形（位移场
 * 的 `totalBounds()`），不需要像自由变换那样处理"旧 bounds ∪ 新 bounds"。
 */
import { TILE, tileKey, type LayerId } from '../tile/key';
import { BYTES_PER_TILE, type TileStore } from '../tile/store';
import type { StrokeTransaction } from '../brush/buffer';
import { padRect, type DisplacementField, type Rect } from './field';
import { warpRegion } from './warp';

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/**
 * 从 TileStore 展平一块矩形区域的 RGBA8 像素（未分配的 tile 视为透明）。
 * 提交（本文件）与实时预览（`interaction/tools/liquify-tool.ts`）共用同一
 * 段读取逻辑——会话期间 TileStore 里的内容就是"液化前的原始像素"（提交
 * 前不会有任何写入），预览每次按需读，不需要单独维护一份快照。
 */
export function flattenLayerRegion(store: TileStore, layer: LayerId, bounds: Rect): Uint8Array {
  // 变量名故意不用 w/h——不是整图分配，是液化会话累积位移场的外接矩形
  // （通常远小于文档），命名要让 test/engine/tile/store.test.ts 的整图
  // 扫描一眼看出这不是它要拦的那类问题，不是靠正则的碰运气
  const regionW = bounds.width;
  const regionH = bounds.height;
  const out = new Uint8Array(regionW * regionH * 4);
  for (let y = 0; y < regionH; y++) {
    const docY = bounds.y + y;
    const ty = Math.floor(docY / TILE);
    for (let x = 0; x < regionW; x++) {
      const docX = bounds.x + x;
      const tx = Math.floor(docX / TILE);
      const tile = store.get(tileKey(layer, tx, ty));
      const data = tile?.data;
      if (data == null) continue;
      const from = ((docY - ty * TILE) * TILE + (docX - tx * TILE)) * 4;
      const to = (y * regionW + x) * 4;
      out[to] = data[from]!;
      out[to + 1] = data[from + 1]!;
      out[to + 2] = data[from + 2]!;
      out[to + 3] = data[from + 3]!;
    }
  }
  return out;
}

export interface WarpedRegionResult {
  readonly dirty: ReadonlyArray<{ tx: number; ty: number }>;
}

/**
 * 把 `output`（`bounds` 尺寸的 warp 结果）touch→capture→逐块写回
 * `layer`。液化提交（本文件）与操控变形提交
 * （`engine/puppet/bake.ts`）共用同一段"结果矩形已知、直接整块写回"逻辑——
 * 两者的差异只在"怎么算出 output"，写回这一步完全一致，没有理由各写一份。
 */
export function writeWarpedRegionToTiles(
  store: TileStore,
  layer: LayerId,
  bounds: Rect,
  output: Uint8Array,
  transaction: StrokeTransaction,
): WarpedRegionResult {
  const regionW = bounds.width;
  const dirtySet = new Map<string, { tx: number; ty: number }>();
  const tx0 = Math.floor(bounds.x / TILE);
  const ty0 = Math.floor(bounds.y / TILE);
  const tx1 = Math.floor((bounds.x + bounds.width - 1) / TILE);
  const ty1 = Math.floor((bounds.y + bounds.height - 1) / TILE);
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) dirtySet.set(`${tx} ${ty}`, { tx, ty });
  }
  for (const { tx, ty } of dirtySet.values()) transaction.touch(tileKey(layer, tx, ty));
  transaction.capture();

  for (const { tx, ty } of dirtySet.values()) {
    const key = tileKey(layer, tx, ty);
    const existing = store.get(key)?.data;
    const out = new Uint8Array(BYTES_PER_TILE);
    if (existing != null) out.set(existing);

    const left = Math.max(bounds.x, tx * TILE);
    const top = Math.max(bounds.y, ty * TILE);
    const right = Math.min(bounds.x + bounds.width, (tx + 1) * TILE);
    const bottom = Math.min(bounds.y + bounds.height, (ty + 1) * TILE);
    for (let y = top; y < bottom; y++) {
      for (let x = left; x < right; x++) {
        const from = ((y - bounds.y) * regionW + (x - bounds.x)) * 4;
        const to = ((y - ty * TILE) * TILE + (x - tx * TILE)) * 4;
        out[to] = output[from]!;
        out[to + 1] = output[from + 1]!;
        out[to + 2] = output[from + 2]!;
        out[to + 3] = output[from + 3]!;
      }
    }
    store.set(key, out);
  }

  return { dirty: [...dirtySet.values()] };
}

export interface LiquifyBakeResult {
  readonly bounds: Rect;
  readonly dirty: ReadonlyArray<{ tx: number; ty: number }>;
}

export interface LiquifyBakeOptions {
  readonly signal?: AbortSignal;
}

/**
 * 把 `field` 累积的位移一次性烘进 `layer` 的像素。空场（未拖动过）返回
 * `null`，不触碰 TileStore、不声明任何事务块。
 */
export async function bakeLiquify(
  store: TileStore,
  layer: LayerId,
  field: DisplacementField,
  transaction: StrokeTransaction,
  options: LiquifyBakeOptions = {},
): Promise<LiquifyBakeResult | null> {
  const raw = field.totalBounds();
  if (raw === null || raw.width <= 0 || raw.height <= 0) return null;

  // 采样区域必须比"位移场非零的地方"更大：推模式的位移量不受 radius 约束
  // （见 field.ts 的 #maxMagnitude 注释），矩形边缘的输出像素常常要去读
  // 矩形外、但仍在图层范围内的有效像素——不加这层 padding 会被误判成
  // "越界"返回透明，画面上就是一块黑洞
  const margin = Math.ceil(field.maxMagnitude()) + 1;
  const bounds = padRect(raw, margin, field.docWidth, field.docHeight);
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  throwIfAborted(options.signal);

  // 1. 展平源区域
  const regionW = bounds.width;
  const regionH = bounds.height;
  const source = flattenLayerRegion(store, layer, bounds);
  throwIfAborted(options.signal);

  // 2. 展平位移场，重采样
  const dx = new Float32Array(regionW * regionH);
  const dy = new Float32Array(regionW * regionH);
  field.flattenTo(dx, dy, bounds);
  const output = await warpRegion(source, regionW, regionH, dx, dy, options);
  throwIfAborted(options.signal);

  // 3. touch → capture → 写回（bounds 覆盖的所有块，源=目标同一网格）
  const { dirty } = writeWarpedRegionToTiles(store, layer, bounds, output, transaction);
  return { bounds, dirty };
}
